import { resolveMarket } from "@/lib/markets/resolve-market";
import {
  getRouteKeyFromSlug,
  makeLocalizedHrefNoLocale,
  makePublicHref,
  stripVisibleLocalePrefix,
} from "@/lib/localizedRoutes/routeHelpers";
import type {
  ExperienceResolution,
  ExperienceSource,
  StoredExperience,
} from "./experience-types";

export function resolveRouteExperience({
  pathname,
  locale,
  domain,
  storedExperience,
  storedExperienceSource,
}: {
  pathname: string;
  locale: string;
  domain: string;
  storedExperience?: StoredExperience | null;
  storedExperienceSource?: ExperienceSource | null;
}): ExperienceResolution {
  const market = resolveMarket({ domain, locale });
  const pathnameWithoutLocalePrefix = stripVisibleLocalePrefix({
    domain,
    pathname,
  });

  const segments = pathnameWithoutLocalePrefix.split("/").filter(Boolean);
  const routeKey = getRouteKeyFromSlug(locale, segments[0]);

  const internationalPathname = market.isGlobal
    ? "/"
    : makeLocalizedHrefNoLocale(locale, "international-real-estate");

  const internationalHref = makePublicHref({
    domain,
    locale,
    pathname: internationalPathname,
  });

  const localHomeHref = makePublicHref({
    domain,
    locale,
    pathname: "/",
  });

  let routeScope: ExperienceResolution["routeScope"] = "neutral";
  let experienceToStore: StoredExperience | null = null;

  if (segments.length === 0) {
    routeScope = market.isGlobal ? "international" : "local";
    experienceToStore = routeScope;
  } else if (routeKey === "international-real-estate") {
    routeScope = "international";
    experienceToStore = "international";
  } else if (routeKey === "search") {
    routeScope = market.isGlobal ? "international" : "local";
    experienceToStore = routeScope;
  }

  const effectiveExperience =
    experienceToStore ||
    storedExperience ||
    (market.isGlobal ? "international" : "local");

  const source: ExperienceSource = experienceToStore
    ? "url"
    : storedExperience
      ? storedExperienceSource || "cookie"
      : "domain-default";

  return {
    routeScope,
    effectiveExperience,
    experienceToStore,
    shouldStoreExperience: !!experienceToStore,
    homeHref:
      effectiveExperience === "international"
        ? internationalHref
        : localHomeHref,
    internationalHref,
    marketCountryCode: market.countryCode,
    isGlobalDomain: market.isGlobal,
    source,
  };
}
